"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import axios from "axios"
import server from "../../utils/serverConfig"
import { debounce } from "lodash"
import StockDistributionTable from "./StockDistributionTable"
import { encryptApiKey } from "../../utils/cryptoUtils"

const StockDistribution = ({
  setAdviceEntries,
  adviceEntries,
  minimumPortfolioAmount,
  setSaveButton,
  readOnly = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleAddAdviceEntry = () => {
    if (readOnly) return

    const newEntry = {
      symbol: "",
      exchange: "",
      segment: "EQUITY",
      inputValue: "",
      symbols: [],
      value: 0,
    }
    setAdviceEntries([newEntry, ...adviceEntries])
    setSaveButton(false)
  }

  const handleRemoveAdviceEntry = (index) => {
    if (readOnly) return

    const updatedEntries = adviceEntries.filter((_, i) => i !== index)
    setAdviceEntries(updatedEntries)
    setSaveButton(false)
  }

  const handleAdviceChange = (index, field, value) => {
    if (readOnly) return

    const updatedEntries = [...adviceEntries]
    updatedEntries[index][field] = Number(value) // Ensure value is a number
    setAdviceEntries(updatedEntries)
  }

  const handleIncrement = (index) => {
    if (readOnly) return

    const updatedEntries = adviceEntries.map((entry, i) =>
      i === index ? { ...entry, value: (Number.parseFloat(entry.value) || 0) + 1 } : entry,
    )
    setAdviceEntries(updatedEntries)
    setSaveButton(false)
  }

  const handleDecrement = (index) => {
    if (readOnly) return

    const updatedEntries = adviceEntries.map((entry, i) =>
      i === index ? { ...entry, value: Math.max((Number.parseFloat(entry.value) || 0) - 1, 0) } : entry,
    )
    setAdviceEntries(updatedEntries)
    setSaveButton(false)
  }

  const fetchSymbols = async (index, inputValue) => {
    if (readOnly) return

    const data = JSON.stringify({
      symbol: inputValue,
      indices: false,
    })

    const config = {
      method: "post",
      maxBodyLength: Number.POSITIVE_INFINITY,
      url: `${server.ccxtServer.baseUrl}angelone/equity/symbol-search`,
      headers: {
        "Content-Type": "application/json",
        "X-Advisor-Subdomain": process.env.REACT_APP_URL,
        "aq-encrypted-key": encryptApiKey(process.env.REACT_APP_AQ_KEYS, process.env.REACT_APP_AQ_SECRET),
      },
      data: data,
    }

    try {
      const response = await axios.request(config)
      setAdviceEntries((prevEntries) =>
        prevEntries.map((entry, i) => (i === index ? { ...entry, symbols: response.data.match } : entry)),
      )
    } catch (error) {
      console.error(error)
    }
  }

  const debouncedFetchSymbols = useCallback(
    debounce((index, value) => {
      if (readOnly) return;
      fetchSymbols(index, value);
    }, 300),
    [readOnly]
  );

  const handleInputChange = (index, value) => {
    if (readOnly) return;
    
    setAdviceEntries((prevEntries) =>
      prevEntries.map((entry, i) =>
        i === index ? { ...entry, inputValue: value, symbol: value } : entry
      )
    );

    if (value.length >= 3) {
      debouncedFetchSymbols(index, value);
    }
  };

  const handleSymbolSelect = (index, symbol, exchange) => {
    if (readOnly) return

    const updatedEntries = adviceEntries.map((entry, i) =>
      i === index
        ? {
            ...entry,
            symbol: symbol,
            symbols: [],
            inputValue: symbol,
            exchange: exchange,
          }
        : entry,
    )
    setAdviceEntries(updatedEntries)
  }

  return (
    <div>
      <div className="flex flex-col space-y-3 h-[400px]  py-4 ">
        <StockDistributionTable
          handleRemoveAdviceEntry={handleRemoveAdviceEntry}
          handleAddAdviceEntry={handleAddAdviceEntry}
          adviceEntries={adviceEntries}
          handleInputChange={handleInputChange}
          handleSymbolSelect={handleSymbolSelect}
          handleAdviceChange={handleAdviceChange}
          handleIncrement={handleIncrement}
          handleDecrement={handleDecrement}
          minimumPortfolioAmount={minimumPortfolioAmount}
          isOpen={isOpen}
          dropdownRef={dropdownRef}
          readOnly={readOnly}
        />
      </div>
    </div>
  )
}

export default StockDistribution
